import { useState, useMemo, useCallback } from 'react';
import { TableState,TableData } from './OCVTypes';

export const useTableManager = (initialData: TableData) => {
  const [state, setState] = useState<TableState>({
    ...initialData,
    sortColumn: -1,
    sortDirection: 'asc',  
    currentPage: 0,
    pageSize: 10,
    totalRows: initialData.Item2?.length || 0,
    showAllRows: false,
    isEmpty : false,
  });

  const sortedData = useMemo(() => {
    if (state.sortColumn === -1) return state.Item2;;
    
    return [...state.Item2].sort((a, b) => {
      const aVal = a[state.sortColumn];
      const bVal = b[state.sortColumn];
      
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      const comparison = aVal < bVal ? -1 : 1;
      return state.sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [...state.Item2, state.sortColumn, state.sortDirection]);

  const paginatedData = useMemo(() => {
    const rows = state.sortColumn === -1 ? state.Item2 : sortedData;
    
    // Return all rows if showAllRows is true
    if (state.showAllRows) {
      return rows;
    }

    const startIdx = state.currentPage * state.pageSize;
    const endIdx = Math.min(startIdx + state.pageSize, state.totalRows);
    return rows.slice(startIdx, endIdx);
  }, [sortedData, state.currentPage, state.pageSize, state.totalRows, state.showAllRows]);

  const handleSort = useCallback((columnIndex: number) => {
    setState(prev => ({
      ...prev,
      sortColumn: columnIndex,
      sortDirection: prev.sortColumn === columnIndex && prev.sortDirection === 'asc' ? 'desc' : 'asc',
      currentPage: 0
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: Math.max(0, Math.min(page, Math.ceil(prev.totalRows / prev.pageSize) - 1))
    }));
  }, []);

  const handlePageSizeChange = useCallback((size: number, showAll: boolean = false) => {
    setState(prev => ({
      ...prev,
      pageSize: size,
      showAllRows: showAll,
      currentPage: 0 // Reset to first page when changing page size
    }));
  }, []);

  return {
    state,
    setState,
    paginatedData,
    handleSort,
    handlePageChange,
    handlePageSizeChange
  };
};